import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  Button,
  Container,
  FormControl,
  InputGroup,
  Modal,
  Row,
  Col,
  Stack,
} from 'react-bootstrap';
import { GiBookCover, GiMagicLamp } from 'react-icons/gi';
import { IoTrashOutline } from 'react-icons/io5';
import { BiSearch } from 'react-icons/bi';
import { getLanguage, useTranslation } from '../i18n';
import { APIClient } from '../lib/APIClient';
import { getSavedBooks, removeBook } from '../lib/Library';
import { SavedBook } from '../interfaces/SavedBook';
import { SHARE_CHANNELS, shareStory, renderButton } from './Share';
import Header from './Header';
import Loading from './Loading';

const apiClient = new APIClient();

function msToReadableDelta(ms: number): string {
  const nowMs = Date.now();
  const deltaMs = nowMs - ms;
  const seconds = Math.floor(deltaMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) {
    if (getLanguage() === 'he') return days > 1 ? `${days} ימים` : 'יום';

    return `${days} day${days > 1 ? 's' : ''}  ago`;
  }
  if (hours > 0) {
    if (getLanguage() === 'he') return hours > 1 ? `${hours} שעות` : 'שעה';

    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  if (minutes > 0) {
    if (getLanguage() === 'he') return minutes > 1 ? `${minutes} דקות` : 'דקה';

    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  return 'less than a minute ago';
}

function ViewLibrary() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [allBooks, setAllBooks] = useState<SavedBook[] | null>(null);
  const [filteredBooks, setFilteredBooks] = useState<SavedBook[] | null>(null);
  const [bookIdToRemove, setBookIdToRemove] = useState<string | null>(null);

  const refresh = () => {
    const allSavedBooks = getSavedBooks();
    allSavedBooks.sort((a, b) => (b.createdAtMs - a.createdAtMs));
    setAllBooks(allSavedBooks);
    setFilteredBooks(allSavedBooks);
  };

  const handleSearchTextChange = useCallback((newSearchText: string) => {
    setSearchText(newSearchText);
    const cleanSearchText = newSearchText.trim().toLowerCase();
    const books = (allBooks || []).filter((book) => {
      if (!cleanSearchText) {
        return true;
      }
      const haystack = `${book.title} ${book.plot} ${book.bookId}`;
      return haystack.toLowerCase().includes(cleanSearchText);
    });
    setFilteredBooks(books);
  }, [allBooks]);

  useEffect(() => {
    refresh();
  }, []);

  let body = <Loading />;

  if (filteredBooks && allBooks) {
    const totalBookCount = allBooks.length;
    let bookList = (
      <div style={{ textAlign: 'center' }}>
        <h4>{t('emptyLibrary')}</h4>
        <p>{t('noStoriesSaved')}</p>
        <p>{t('createNewOne')}</p>
        <Button
          onClick={() => navigate('/')}
          variant="success"
          className="btn-lg"
        >
          <GiMagicLamp />
          {' '}
          {t('newStory')}
        </Button>
      </div>
    );

    if (filteredBooks.length > 0) {
      bookList = (
        <Accordion>
          {
            filteredBooks.map((book) => (
              <Accordion.Item eventKey={book.bookId} key={book.bookId}>
                <Accordion.Header style={{ direction: getLanguage() === 'he' ? 'rtl' : 'ltr' }}>{book.title}</Accordion.Header>
                <Accordion.Body>
                  <Container>
                    <Row>
                      <Col md="auto">
                        <img
                          src={apiClient.getPageImageUrl(book.bookId, 1)}
                          height="200"
                          alt="story cover"
                        />
                      </Col>
                      <Col>
                        <Stack style={{ height: '85%' }}>
                          <Stack direction="horizontal">
                            <div>{book.plot}</div>
                            <Stack direction="horizontal" className={getLanguage() === 'he' ? 'me-auto' : 'ms-auto'}>
                              {
                                SHARE_CHANNELS.map((channel, index) => (
                                  <div key={channel.name} className={index ? 'ms-1' : 'ms-auto'}>
                                    {
                                      renderButton(
                                        channel,
                                        () => shareStory(channel, book.bookId, book.title),
                                      )
                                    }
                                  </div>
                                ))
                              }
                            </Stack>
                          </Stack>
                          <Button
                            variant="success"
                            className={getLanguage() === 'he' ? 'ms-auto m-2' : 'me-auto m-2'}
                            onClick={() => navigate(`/book/${book.bookId}`)}
                          >
                            <GiBookCover />
                            {' '}
                            {t('letsRead')}
                          </Button>
                        </Stack>
                        <Stack direction="horizontal">
                          <div className="mt-2">
                            <i>
                              {book.amIAuthor ? t('createdByMe') : t('readByMe')}
                              {' '}
                              {msToReadableDelta(book.createdAtMs)}
                            </i>
                          </div>
                          <Button
                            onClick={() => setBookIdToRemove(book.bookId)}
                            variant="danger"
                            size="sm"
                            className={getLanguage() === 'he' ? 'me-auto' : 'ms-auto'}
                          >
                            <IoTrashOutline />
                            {' '}
                            {t('removeStory')}
                          </Button>
                        </Stack>
                      </Col>
                    </Row>
                  </Container>
                </Accordion.Body>
              </Accordion.Item>
            ))
          }
        </Accordion>
      );
    }

    body = (
      <>
        <Modal centered show={!!bookIdToRemove} onHide={() => setBookIdToRemove(null)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('removeBookTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t('removeBookConfirm')}</p>
            <p>{t('removeBookNote')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setBookIdToRemove(null)}>
              {t('cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                removeBook(bookIdToRemove || '');
                setBookIdToRemove(null);
                refresh();
              }}
            >
              Remove
            </Button>
          </Modal.Footer>
        </Modal>
        <p>
          {` ${totalBookCount === 1 ? t('story') : t('youHave') + totalBookCount + t('storiesInLibrary')} `}

        </p>
        <InputGroup className="mb-3">
          <InputGroup.Text><BiSearch /></InputGroup.Text>
          <FormControl
            placeholder={t('searchLibrary')}
            value={searchText}
            onChange={(event) => handleSearchTextChange(event.target.value)}
          />
          <InputGroup.Text>
            {t('showing')}
            {' '}
            {filteredBooks.length}
            /
            {totalBookCount}
          </InputGroup.Text>
        </InputGroup>
        {bookList}
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="m-2" style={{ direction: getLanguage() === 'he' ? 'rtl' : 'ltr' }}>
        <h1>{t('library')}</h1>
        {body}
      </div>
    </>
  );
}

export default ViewLibrary;
